import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { adminPlanTierApi } from '../../api/adminSubscriptionPlan.api';
import { apiHandler, unwrapData } from '../../utils/apiHandler';
import { formatPriceChip } from './planFormatters';
import './subscription-plans.css';

export default function PlanTierList() {
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    const result = await apiHandler(() => adminPlanTierApi.list());
    if (result.success) {
      setTiers(unwrapData(result) || []);
    } else {
      setError(result.message || 'Failed to load plans');
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const handleDeactivate = async (tier) => {
    if (!window.confirm(`Deactivate "${tier.name}"?`)) return;
    const result = await apiHandler(() => adminPlanTierApi.deactivate(tier.id));
    if (result.success) {
      load();
    } else {
      setError(result.message || 'Failed to deactivate plan');
    }
  };

  return (
    <div className="sp-page">
      <PageHeader
        title="Subscription Plans"
        subtitle="Manage billing plans, pricing, alert templates, and plan notes"
        actions={(
          <div className="sp-header-actions">
            <Link to="/plan-addons" className="sp-btn sp-btn-secondary">
              <i className="fa-solid fa-puzzle-piece" /> Plan Add-ons
            </Link>
            <button type="button" className="sp-btn sp-btn-primary" onClick={() => navigate('/plans/new/edit')}>
              <i className="fa-solid fa-plus" /> Create plan
            </button>
          </div>
        )}
      />

      {error && <div className="sp-alert sp-alert-danger">{error}</div>}

      <div className="sp-table-wrap">
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table sp-plans-table">
            <thead>
              <tr>
                <th>Plan</th>
                <th>Code</th>
                <th>Prices</th>
                <th>Members</th>
                <th>Subscribers</th>
                <th>Popular</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="sp-table-empty">Loading plans…</td>
                </tr>
              ) : tiers.length === 0 ? (
                <tr>
                  <td colSpan={8} className="sp-table-empty">No plans found.</td>
                </tr>
              ) : (
                tiers.map((tier) => {
                  const activePrices = (tier.prices || []).filter((p) => p.isActive !== false);
                  return (
                    <tr key={tier.id}>
                      <td>
                        <div className="sp-table-plan-name">
                          <strong>{tier.name}</strong>
                          {tier.subtitle && <span>{tier.subtitle}</span>}
                        </div>
                      </td>
                      <td><code className="sp-code">{tier.tier}</code></td>
                      <td>
                        <div className="sp-table-prices">
                          {activePrices.length === 0 ? (
                            <span className="sp-muted">No prices</span>
                          ) : (
                            <>
                              <span className="sp-price-count">
                                {activePrices.length} price{activePrices.length === 1 ? '' : 's'}
                              </span>
                              <div className="sp-price-chip-row">
                                {activePrices
                                  .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
                                  .map((price) => (
                                    <span
                                      key={price.id}
                                      className={`sp-list-price-chip sp-list-price-chip--${price.interval}`}
                                    >
                                      {formatPriceChip(price)}
                                    </span>
                                  ))}
                              </div>
                            </>
                          )}
                        </div>
                      </td>
                      <td>{tier.includedMembers ?? 1}</td>
                      <td>{tier.subscriberCount || 0}</td>
                      <td>
                        {tier.isPopular ? (
                          <span className="sp-badge sp-badge-popular">Popular</span>
                        ) : (
                          <span className="sp-muted">—</span>
                        )}
                      </td>
                      <td>
                        <span className={`sp-badge ${tier.isActive ? 'sp-badge-active' : 'sp-badge-inactive'}`}>
                          {tier.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div className="sp-table-actions">
                          <button
                            type="button"
                            className="sp-btn sp-btn-secondary sp-btn-sm"
                            onClick={() => navigate(`/plans/${tier.id}/edit`)}
                          >
                            Edit
                          </button>
                          {tier.isActive && (
                            <button
                              type="button"
                              className="sp-btn sp-btn-ghost sp-btn-sm"
                              onClick={() => handleDeactivate(tier)}
                            >
                              Deactivate
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
